/**
 * Shared Storage Service
 * 
 * Utilities for localStorage and sessionStorage with type safety.
 * 
 * Features:
 * - Type-safe get/set operations
 * - JSON serialization/deserialization
 * - TTL (Time To Live) support
 * - Prefix namespacing to avoid conflicts
 * 
 * @example
 * ```tsx
 * import { storage } from '@/shared/services';
 * 
 * // Store data
 * storage.set('user', { id: 1, name: 'John' });
 * 
 * // Retrieve data
 * const user = storage.get<User>('user');
 * 
 * // Store with TTL (expires in 1 hour)
 * storage.set('token', 'abc123', { ttl: 3600000 });
 * ```
 */

// =============================================================================
// TYPES
// =============================================================================

interface StorageItem<T> {
  value: T;
  expiry?: number;
}

interface SetOptions {
  /** Time to live in milliseconds */
  ttl?: number;
  /** Use sessionStorage instead of localStorage */
  session?: boolean;
}

// =============================================================================
// STORAGE SERVICE
// =============================================================================

const PREFIX = 'haat_hub_';
const isDev = process.env.NODE_ENV === 'development';

/**
 * Get the appropriate storage object
 */
function getStorage(session: boolean = false): Storage {
  return session ? sessionStorage : localStorage;
}

/**
 * Build prefixed key
 */
function buildKey(key: string): string {
  return `${PREFIX}${key}`;
}

/**
 * Storage service with type-safe operations
 */
export const storage = {
  /**
   * Get a value from storage
   */
  get<T>(key: string, options: { session?: boolean } = {}): T | null {
    try {
      const storage = getStorage(options.session);
      const item = storage.getItem(buildKey(key));
      
      if (!item) return null;

      const parsed: StorageItem<T> = JSON.parse(item);

      // Check expiry
      if (parsed.expiry && Date.now() > parsed.expiry) {
        this.remove(key, options);
        return null;
      }

      return parsed.value;
    } catch (error) {
      if (isDev) console.error(`[Storage] Error getting key "${key}":`, error);
      return null;
    }
  },

  /**
   * Set a value in storage
   */
  set<T>(key: string, value: T, options: SetOptions = {}): void {
    try {
      const storage = getStorage(options.session);
      const item: StorageItem<T> = {
        value,
        expiry: options.ttl ? Date.now() + options.ttl : undefined,
      };
      
      storage.setItem(buildKey(key), JSON.stringify(item));
    } catch (error) {
      if (isDev) console.error(`[Storage] Error setting key "${key}":`, error);
    }
  },

  /**
   * Remove a value from storage
   */
  remove(key: string, options: { session?: boolean } = {}): void {
    try {
      const storage = getStorage(options.session);
      storage.removeItem(buildKey(key));
    } catch (error) {
      if (isDev) console.error(`[Storage] Error removing key "${key}":`, error);
    }
  },

  /**
   * Clear all values with our prefix
   */
  clearAll(options: { session?: boolean } = {}): void {
    try {
      const storage = getStorage(options.session);
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
    } catch (error) {
      if (isDev) console.error('[Storage] Error clearing storage:', error);
    }
  },

  /**
   * Check if a key exists and is not expired
   */
  has(key: string, options: { session?: boolean } = {}): boolean {
    return this.get(key, options) !== null;
  },

  /**
   * Get all keys with our prefix
   */
  keys(options: { session?: boolean } = {}): string[] {
    try {
      const storage = getStorage(options.session);
      const keys: string[] = [];
      
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(PREFIX)) {
          keys.push(key.replace(PREFIX, ''));
        }
      }

      return keys;
    } catch (error) {
      if (isDev) console.error('[Storage] Error getting keys:', error);
      return [];
    }
  },
};

// =============================================================================
// SPECIALIZED STORAGE HELPERS
// =============================================================================

/**
 * User preferences storage
 */
export const userPreferences = {
  get<T>(key: string): T | null {
    return storage.get<T>(`pref_${key}`);
  },

  set<T>(key: string, value: T): void {
    storage.set(`pref_${key}`, value);
  },

  remove(key: string): void {
    storage.remove(`pref_${key}`);
  },
};

/**
 * Cache storage with default TTL
 */
export const cache = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes

  get<T>(key: string): T | null {
    return storage.get<T>(`cache_${key}`);
  },

  set<T>(key: string, value: T, ttl?: number): void {
    storage.set(`cache_${key}`, value, { ttl: ttl || this.defaultTTL });
  },

  invalidate(key: string): void {
    storage.remove(`cache_${key}`);
  },

  invalidateAll(): void {
    const keys = storage.keys().filter((k) => k.startsWith('cache_'));
    keys.forEach((key) => storage.remove(key));
  },
};

export default storage;
